import {
  Button,
  Calendar,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@1trading/react';
import { Filter } from 'lucide-react';
import { type KeyboardEvent, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { closeOnly } from '@/components/dialogs/close-only';
import {
  BLANK_FILTER_VALUE,
  type ColumnFilterConfig,
  NON_BLANK_FILTER_VALUE,
  parseDateFilter,
  parseNumberFilter,
  type TokenMatchMode,
  type UseColumnFiltersResult,
} from '@/hooks/use-column-filters';
import { toDateInputValue } from '@/utils/date';

// Google-Sheets-style per-column filter: a funnel button in the header cell opening
// a popover whose control depends on the column's filter kind. Applied filters land
// in the same useColumnFilters chip state as the ColumnFilterBar variant, so both
// UIs can drive one filter engine.

const OPTION_SEARCH_THRESHOLD = 8;

export interface ColumnFilterHeaderProps<TRow> {
  config: ColumnFilterConfig<TRow>;
  /** Return value of useColumnFilters(data, configs, ...) for the same configs. */
  state: UseColumnFiltersResult<TRow>;
  /** Distinct values ('choice') or distinct tokens ('tokens') for the checkbox list. */
  options?: string[];
}

const OptionList = ({
  options,
  selected,
  onToggle,
  onToggleAll,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  onToggleAll: (values: string[], checked: boolean) => void;
}) => {
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.toLowerCase().includes(needle));
  }, [options, search]);

  // Select-all works on the searched subset, so a narrowed list can be bulk-toggled too.
  const allVisibleSelected = visible.length > 0 && visible.every((option) => selected.includes(option));
  const someVisibleSelected = visible.some((option) => selected.includes(option));

  return (
    <div className="flex flex-col gap-2">
      {options.length > OPTION_SEARCH_THRESHOLD ? <Input className="h-8" onChange={(event) => setSearch(event.target.value)} placeholder="Search values…" value={search} /> : null}
      {visible.length > 0 ? (
        <Label className="flex cursor-pointer items-center gap-2 rounded border-b px-1 pt-1 pb-2 font-normal hover:bg-muted">
          <Checkbox checked={allVisibleSelected || (someVisibleSelected && 'indeterminate')} onCheckedChange={(checked) => onToggleAll(visible, checked === true)} />
          <span className="text-xs">Select all{search.trim() ? ' (matching)' : ''}</span>
        </Label>
      ) : null}
      <div className="flex max-h-56 flex-col gap-1 overflow-y-auto pr-1">
        {visible.map((option) => (
          <Label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 font-normal hover:bg-muted" key={option}>
            <Checkbox checked={selected.includes(option)} onCheckedChange={(checked) => onToggle(option, checked === true)} />
            <span className="truncate font-mono text-xs">{option}</span>
          </Label>
        ))}
        {visible.length === 0 ? <p className="px-1 py-2 text-muted-foreground text-xs">No matching values.</p> : null}
      </div>
    </div>
  );
};
OptionList.displayName = 'OptionList';

const TextFilterInput = ({ onSubmit }: { onSubmit: (value: string) => void }) => {
  const [text, setText] = useState('');
  const submit = () => {
    if (!text.trim()) return;
    onSubmit(text);
    setText('');
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    submit();
  };
  return (
    <div className="flex items-center gap-2">
      <Input autoFocus className="h-8" onChange={(event) => setText(event.target.value)} onKeyDown={handleKeyDown} placeholder="Contains…" value={text} />
      <Button disabled={!text.trim()} onClick={submit} size="sm" type="button" variant="outline">
        Add
      </Button>
    </div>
  );
};
TextFilterInput.displayName = 'TextFilterInput';

const NumberFilterInput = ({ onSubmit }: { onSubmit: (value: string) => void }) => {
  const [op, setOp] = useState<'>' | '<'>('>');
  const [amount, setAmount] = useState('');
  // Validity comes from the same parser the filter engine uses, so an accepted chip can never silently match nothing.
  const chipValue = `${op}${amount.trim()}`;
  const isValid = parseNumberFilter(chipValue) !== null;
  const submit = () => {
    if (!isValid) return;
    onSubmit(chipValue);
    setAmount('');
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    submit();
  };
  return (
    <div className="flex items-center gap-2">
      <Select onValueChange={(value) => setOp(value as '>' | '<')} value={op}>
        <SelectTrigger className="h-8 w-20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value=">">over</SelectItem>
          <SelectItem value="<">under</SelectItem>
        </SelectContent>
      </Select>
      <Input autoFocus className="h-8" inputMode="decimal" onChange={(event) => setAmount(event.target.value)} onKeyDown={handleKeyDown} placeholder="Amount…" value={amount} />
      <Button disabled={!isValid} onClick={submit} size="sm" type="button" variant="outline">
        Add
      </Button>
    </div>
  );
};
NumberFilterInput.displayName = 'NumberFilterInput';

// Rolling-window presets; "last month"/"last year" style filtering uses the rolling
// 30/365-day equivalents, and calendar-exact windows live in the Custom dialog.
const DATE_PRESETS = ['last 7 days', 'last 30 days', 'last 90 days', 'last 365 days'];

interface DateFilterControlProps {
  /** The column's active date-expression chip (sentinels excluded); undefined when none. */
  activeValue: string | undefined;
  /** Replace the column's date chip (null clears it); the blanks toggles are untouched. */
  onReplace: (value: string | null) => void;
  onOpenCustom: () => void;
}

/**
 * Radio-style preset buttons: a date column holds at most one date chip, so picking a
 * preset (or applying a custom window/range) replaces the previous one, and clicking
 * the active preset again clears it.
 */
const DateFilterControl = ({ activeValue, onReplace, onOpenCustom }: DateFilterControlProps) => {
  const isCustomActive = activeValue !== undefined && !DATE_PRESETS.includes(activeValue);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-1.5">
        {DATE_PRESETS.map((preset) => (
          <Button
            className="h-7 text-xs capitalize"
            key={preset}
            onClick={() => onReplace(activeValue === preset ? null : preset)}
            size="sm"
            type="button"
            variant={activeValue === preset ? 'default' : 'outline'}
          >
            {preset}
          </Button>
        ))}
      </div>
      <Button className="h-7 text-xs" onClick={onOpenCustom} size="sm" type="button" variant={isCustomActive ? 'default' : 'outline'}>
        <span className="truncate">{isCustomActive ? `Custom: ${activeValue}` : 'Custom…'}</span>
      </Button>
    </div>
  );
};
DateFilterControl.displayName = 'DateFilterControl';

interface CustomDateFilterDialogProps {
  columnTitle: string;
  onApply: (value: string) => void;
  onClose: () => void;
}

type CustomDateMode = 'days' | 'range';

/** Custom date filter: a rolling last-X-days window, or a range picked on a calendar. */
const CustomDateFilterDialog = ({ columnTitle, onApply, onClose }: CustomDateFilterDialogProps) => {
  const [mode, setMode] = useState<CustomDateMode>('days');
  const [days, setDays] = useState('');
  const [range, setRange] = useState<DateRange | undefined>();

  // Validity comes from the same parser the filter engine uses, so an accepted chip can never silently match nothing.
  const chip =
    mode === 'days'
      ? `last ${days.trim()} days`
      : [range?.from && `from ${toDateInputValue(range.from)}`, range?.to && `to ${toDateInputValue(range.to)}`].filter(Boolean).join(' ');
  const isValid = chip !== '' && parseDateFilter(chip) !== null;
  const apply = () => {
    if (!isValid) return;
    onApply(chip);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    apply();
  };

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open>
      <DialogContent className="sm:w-fit sm:min-w-[21rem] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Filter {columnTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <ToggleGroup
            className="w-full"
            onValueChange={(value) => {
              if (value) setMode(value as CustomDateMode);
            }}
            size="sm"
            type="single"
            value={mode}
            variant="outline"
          >
            <ToggleGroupItem className="flex-1 text-xs" value="days">
              Rolling window
            </ToggleGroupItem>
            <ToggleGroupItem className="flex-1 text-xs" value="range">
              Date range
            </ToggleGroupItem>
          </ToggleGroup>
          {mode === 'days' ? (
            <InputGroup>
              <InputGroupAddon align="inline-start">
                <InputGroupText>Last</InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Last X days"
                autoFocus
                inputMode="numeric"
                onChange={(event) => setDays(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="30"
                value={days}
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText>days</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Calendar className="rounded-md border" mode="range" numberOfMonths={1} onSelect={setRange} selected={range} />
              <p className="text-muted-foreground text-xs tabular-nums">
                {range?.from ? `${toDateInputValue(range.from)} → ${range.to ? toDateInputValue(range.to) : '…'}` : 'Pick a start day, then an end day.'}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={!isValid} onClick={apply} type="button">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
CustomDateFilterDialog.displayName = 'CustomDateFilterDialog';

export const ColumnFilterHeader = <TRow,>({ config, state, options = [] }: ColumnFilterHeaderProps<TRow>) => {
  const { filters, addFilter, setFieldValues, removeFilter, clearField, tokenModes, setTokenMode } = state;
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const selected = filters.filter((filter) => filter.field === config.field).map((filter) => filter.value);
  const isActive = selected.length > 0;
  const mode: TokenMatchMode = tokenModes[config.field] ?? 'any';

  const toggleValue = (value: string, checked: boolean) => {
    if (checked) addFilter(config.field, value);
    else removeFilter({ field: config.field, value });
  };

  // One state update for the whole batch; selections outside the toggled set survive.
  const toggleAll = (values: string[], checked: boolean) => {
    const next = checked ? [...selected, ...values] : selected.filter((value) => !values.includes(value));
    setFieldValues(config.field, next);
  };

  // A date column carries at most one date-expression chip; replacing it leaves the blanks toggles alone.
  const activeDateChip = config.filter === 'date' ? selected.find((value) => parseDateFilter(value) !== null) : undefined;
  const replaceDateChip = (value: string | null) => {
    const sentinels = selected.filter((selectedValue) => parseDateFilter(selectedValue) === null);
    setFieldValues(config.field, value ? [...sentinels, value] : sentinels);
  };

  // The dialog is a sibling of the Popover, not a child of its content: opening it moves
  // focus out of the popover, which dismisses (and unmounts) the popover's subtree.
  return (
    <>
      {isCustomDateOpen ? (
        <CustomDateFilterDialog
          columnTitle={config.title}
          onApply={(value) => {
            replaceDateChip(value);
            setIsCustomDateOpen(false);
          }}
          onClose={() => setIsCustomDateOpen(false)}
        />
      ) : null}
      <Popover>
        <PopoverTrigger asChild>
          <button
            aria-label={`Filter ${config.title}${isActive ? ` (${selected.length} active)` : ''}`}
            className={cn('rounded p-1 hover:bg-muted hover:text-foreground', isActive ? 'text-primary' : 'text-muted-foreground/60')}
            type="button"
          >
            <Filter className={cn('size-3.5', isActive && 'fill-current')} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm">{config.title}</span>
              {isActive ? (
                <Button className="h-6 px-2 text-xs" onClick={() => clearField(config.field)} size="sm" type="button" variant="ghost">
                  Clear
                </Button>
              ) : null}
            </div>
            {config.filter === 'tokens' ? (
              <ToggleGroup
                className="justify-start"
                onValueChange={(value) => {
                  if (value) setTokenMode(config.field, value as TokenMatchMode);
                }}
                size="sm"
                type="single"
                value={mode}
              >
                <ToggleGroupItem className="h-7 px-2 text-xs" value="any">
                  Has any
                </ToggleGroupItem>
                <ToggleGroupItem className="h-7 px-2 text-xs" value="all">
                  Has all
                </ToggleGroupItem>
              </ToggleGroup>
            ) : null}
            {config.filter === 'choice' || config.filter === 'tokens' ? <OptionList onToggle={toggleValue} onToggleAll={toggleAll} options={options} selected={selected} /> : null}
            {config.filter === 'text' ? <TextFilterInput onSubmit={(value) => addFilter(config.field, value)} /> : null}
            {config.filter === 'number' ? <NumberFilterInput onSubmit={(value) => addFilter(config.field, value)} /> : null}
            {config.filter === 'date' ? <DateFilterControl activeValue={activeDateChip} onOpenCustom={() => setIsCustomDateOpen(true)} onReplace={replaceDateChip} /> : null}
            {/* Every kind gets the blanks toggles: missing data is filterable — or excludable — even where no distinct-value option exists. */}
            <div className="flex flex-col border-t pt-1">
              <Label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 font-normal hover:bg-muted">
                <Checkbox checked={selected.includes(BLANK_FILTER_VALUE)} onCheckedChange={(checked) => toggleValue(BLANK_FILTER_VALUE, checked === true)} />
                <span className="text-muted-foreground text-xs">{BLANK_FILTER_VALUE}</span>
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 font-normal hover:bg-muted">
                <Checkbox checked={selected.includes(NON_BLANK_FILTER_VALUE)} onCheckedChange={(checked) => toggleValue(NON_BLANK_FILTER_VALUE, checked === true)} />
                <span className="text-muted-foreground text-xs">{NON_BLANK_FILTER_VALUE}</span>
              </Label>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};
ColumnFilterHeader.displayName = 'ColumnFilterHeader';
